<template>
    <div>
        <button
            name="refund"
            class="w-full inline-flex justify-center items-center px-6 py-3 my-2 shadow-sm text-base font-medium rounded-md text-white bg-red-500 hover:bg-red-600 focus:outline-none"
            @click="openModal"
        >
            <ReceiptRefundIcon class="h-5 w-5 mr-2" aria-hidden="true" />
            Refund Order
        </button>

        <TransitionRoot as="template" :show="open">
            <Dialog
                as="div"
                static
                class="fixed z-20 inset-0 overflow-y-auto"
                @close="open = false"
                :open="open"
            >
                <div
                    class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <DialogOverlay
                            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                        />
                    </TransitionChild>

                    <span
                        class="hidden sm:inline-block sm:align-middle sm:h-screen"
                        aria-hidden="true"
                        >&#8203;</span
                    >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div
                            class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
                        >
                            <div
                                class="hidden sm:block absolute top-0 right-0 pt-4 pr-4"
                            >
                                <button
                                    type="button"
                                    class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                                    @click="open = false"
                                >
                                    <span class="sr-only">Close</span>
                                    <XIcon class="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>
                            <div class="sm:flex sm:items-start">
                                <div
                                    class="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <ReceiptRefundIcon
                                        class="h-6 w-6 text-red-600"
                                        aria-hidden="true"
                                    />
                                </div>

                                <div class="mt-3 sm:mt-0 w-full sm:mr-10">
                                    <DialogTitle
                                        as="h3"
                                        class="mt-6 sm:mt-2 text-xl leading-6 font-medium text-gray-800 sm:ml-4 text-center sm:text-left"
                                    >
                                        Refund Order #{{ publicOrderId }}
                                    </DialogTitle>

                                    <div class="mt-6 mb-4 mx-5 sm:mx-4 sm:px-8">
                                        <div
                                            class="mb-2 flex items-baseline justify-between"
                                        >
                                            <dt
                                                class="font-semibold text-gray-500"
                                            >
                                                Subtotal:
                                            </dt>
                                            <dd class="mt-1 text-gray-800">
                                                ${{
                                                    dollarsForDisplay(
                                                        totals.subtotal,
                                                    )
                                                }}
                                            </dd>
                                        </div>

                                        <div
                                            v-if="totals.discounts > 0"
                                            class="mb-2 flex items-baseline justify-between"
                                        >
                                            <dt
                                                class="font-semibold text-gray-500"
                                            >
                                                Discounts:
                                            </dt>
                                            <dd class="mt-1 text-gray-800">
                                                - ${{
                                                    dollarsForDisplay(
                                                        totals.discounts,
                                                    )
                                                }}
                                            </dd>
                                        </div>

                                        <div
                                            class="mb-2 flex items-baseline justify-between"
                                        >
                                            <dt
                                                class="font-semibold text-gray-500"
                                            >
                                                Shipping:
                                            </dt>
                                            <dd class="mt-1 text-gray-800">
                                                <span v-if="totals.shipping > 0"
                                                    >${{
                                                        dollarsForDisplay(
                                                            totals.shipping,
                                                        )
                                                    }}</span
                                                >
                                                <span v-else>FREE</span>
                                            </dd>
                                        </div>

                                        <div
                                            v-if="totals.tax.total > 0"
                                            class="mb-2 flex items-baseline justify-between"
                                        >
                                            <dt
                                                class="font-semibold text-gray-500"
                                            >
                                                Tax:
                                            </dt>
                                            <dd class="mt-1 text-gray-800">
                                                ${{
                                                    dollarsForDisplay(
                                                        totals.tax.total,
                                                    )
                                                }}
                                            </dd>
                                        </div>

                                        <div
                                            v-if="totals.refund > 0"
                                            class="mb-2 flex items-baseline justify-between"
                                        >
                                            <dt
                                                class="font-semibold text-red-800"
                                            >
                                                Refunds:
                                            </dt>
                                            <dd class="mt-1 text-red-800">
                                                - ${{
                                                    dollarsForDisplay(
                                                        totals.refund,
                                                    )
                                                }}
                                            </dd>
                                        </div>

                                        <div
                                            class="mb-2 flex items-baseline justify-between"
                                        >
                                            <dt
                                                class="font-bold text-gray-500 text-xl"
                                            >
                                                Total:
                                            </dt>
                                            <dd
                                                class="mt-1 font-semibold text-xl text-gray-800"
                                            >
                                                ${{
                                                    dollarsForDisplay(
                                                        totals.balance.total,
                                                    )
                                                }}
                                            </dd>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <form
                                ref="form"
                                :action="actionUrl"
                                method="POST"
                                @submit.prevent="onSubmit(form)"
                            >
                                <div
                                    class="px-4 py-3 sm:px-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense"
                                >
                                    <input
                                        type="hidden"
                                        name="_token"
                                        :value="csrf"
                                    />

                                    <div
                                        class="sm:col-span-2 sm:px-16 pb-4 sm:pb-2"
                                    >
                                        <label
                                            for="product_refund"
                                            class="block text-sm font-medium text-gray-700"
                                        >
                                            Product Refund
                                        </label>

                                        <div
                                            class="mt-1 relative rounded-md shadow-sm"
                                        >
                                            <div
                                                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                                            >
                                                <span
                                                    :class="[
                                                        hasError(
                                                            errors,
                                                            'product_refund',
                                                        )
                                                            ? 'text-red-900'
                                                            : 'text-gray-500',
                                                        'sm:text-sm',
                                                    ]"
                                                    >$</span
                                                >
                                            </div>
                                            <input
                                                v-model="inputs.product_refund"
                                                :ref="refs.product_refund"
                                                name="product_refund"
                                                id="product_refund"
                                                type="number"
                                                step="0.01"
                                                :class="[
                                                    hasError(
                                                        errors,
                                                        'product_refund',
                                                    )
                                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                                    'appearance-none block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                                ]"
                                            />
                                        </div>
                                        <p
                                            v-if="
                                                hasError(
                                                    errors,
                                                    'product_refund',
                                                )
                                            "
                                            class="mt-2 text-sm text-red-600"
                                        >
                                            {{
                                                error(errors, "product_refund")
                                            }}
                                        </p>
                                    </div>

                                    <div
                                        class="sm:col-span-2 sm:px-16 pb-4 sm:pb-2"
                                    >
                                        <label
                                            for="shipping_refund"
                                            class="block text-sm font-medium text-gray-700"
                                        >
                                            Shipping Refund
                                        </label>

                                        <div
                                            class="mt-1 relative rounded-md shadow-sm"
                                        >
                                            <div
                                                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                                            >
                                                <span
                                                    :class="[
                                                        hasError(
                                                            errors,
                                                            'shipping_refund',
                                                        )
                                                            ? 'text-red-900'
                                                            : 'text-gray-500',
                                                        'sm:text-sm',
                                                    ]"
                                                    >$</span
                                                >
                                            </div>
                                            <input
                                                v-model="inputs.shipping_refund"
                                                :ref="refs.shipping_refund"
                                                name="shipping_refund"
                                                id="shipping_refund"
                                                type="number"
                                                step="0.01"
                                                :disabled="
                                                    shippingNotRefundable
                                                "
                                                :class="[
                                                    hasError(
                                                        errors,
                                                        'shipping_refund',
                                                    )
                                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                                    shippingNotRefundable
                                                        ? 'bg-gray-50 cursor-not-allowed'
                                                        : '',
                                                    'appearance-none block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                                ]"
                                            />
                                        </div>
                                        <p
                                            v-if="
                                                hasError(
                                                    errors,
                                                    'shipping_refund',
                                                )
                                            "
                                            class="mt-2 text-sm text-red-600"
                                        >
                                            {{
                                                error(errors, "shipping_refund")
                                            }}
                                        </p>
                                    </div>

                                    <div
                                        class="sm:col-span-2 sm:px-16 pb-4 sm:pb-2"
                                    >
                                        <label
                                            for="tax_refund"
                                            class="block text-sm font-medium text-gray-700"
                                        >
                                            Tax Refund
                                        </label>

                                        <div
                                            class="mt-1 relative rounded-md shadow-sm"
                                        >
                                            <div
                                                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                                            >
                                                <span
                                                    class="text-gray-500 sm:text-sm"
                                                    >$</span
                                                >
                                            </div>
                                            <input
                                                :value="
                                                    dollarsForDisplay(
                                                        refundTotals.tax,
                                                    )
                                                "
                                                name="tax_refund"
                                                id="tax_refund"
                                                type="text"
                                                disabled
                                                class="appearance-none block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 cursor-not-allowed focus:outline-none sm:text-sm"
                                            />
                                        </div>
                                    </div>

                                    <div
                                        class="sm:col-span-2 sm:px-16 pb-6 border-t-2 border-gray-200 pt-4"
                                    >
                                        <div
                                            class="flex items-baseline justify-between"
                                        >
                                            <span
                                                class="font-bold text-red-800 text-xl"
                                                >Total Refund:</span
                                            >
                                            <span
                                                class="mt-1 font-semibold text-red-800 text-xl"
                                                >{{
                                                    refundTotals.total > 0
                                                        ? "- $"
                                                        : "$"
                                                }}{{
                                                    dollarsForDisplay(
                                                        refundTotals.total,
                                                    )
                                                }}</span
                                            >
                                        </div>
                                    </div>

                                    <button
                                        name="yes"
                                        type="submit"
                                        class="bg-nutreance-green hover:bg-nutreance-green-darker w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none sm:col-start-2 sm:text-sm"
                                    >
                                        <LoadingIcon
                                            v-if="submitted"
                                            class="h-5 w-5 mr-2"
                                        />
                                        <CheckIcon
                                            v-else
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        Refund
                                    </button>

                                    <button
                                        name="no"
                                        type="button"
                                        class="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-500 hover:bg-red-600 text-base font-medium text-white focus:outline-none sm:mt-0 sm:col-start-1 sm:text-sm"
                                        @click="open = false"
                                    >
                                        <XIcon
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, reactive, ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import { CheckIcon, ReceiptRefundIcon, XIcon } from "@heroicons/vue/solid";
import { useForm } from "../../functions/useForm";
import { useMoney } from "../../functions/useMoney";
import LoadingIcon from "../LoadingIcon.vue";

const props = defineProps<{
    csrf?: string;
    errors?: any;
    old?: any;
    publicOrderId?: string;
    totals?: any;
}>();

const { dollarsForDisplay } = useMoney();
const { error, hasError, oldInput, onSubmit, submitted } = useForm();
const form = ref<any>(null);
const open = ref(
    Object.prototype.hasOwnProperty.call(props.old, "product_refund") ||
        Object.prototype.hasOwnProperty.call(props.old, "shipping_refund"),
);
const actionUrl = "/user/orders/" + props.publicOrderId + "/refund";

const refs = {
    product_refund: ref<any>(null),
    shipping_refund: ref<any>(null),
};

const inputs = reactive({
    product_refund: oldInput(props.old, "product_refund")
        ? oldInput(props.old, "product_refund")
        : (props.totals.balance.product / 100).toFixed(2),
    shipping_refund: oldInput(props.old, "shipping_refund")
        ? oldInput(props.old, "shipping_refund")
        : "0.00",
});

const refundTotals = computed(() => {
    const product = Math.round(parseFloat(inputs.product_refund || 0) * 100);
    const shipping = Math.round(parseFloat(inputs.shipping_refund || 0) * 100);

    const productTax =
        product === 0 || props.totals.original.product === 0
            ? 0
            : Math.round(
                  (product * props.totals.original.productTax) /
                      props.totals.original.product,
              );

    const shippingTax =
        shipping === 0 || props.totals.original.shipping === 0
            ? 0
            : Math.round(
                  (shipping * props.totals.original.shippingTax) /
                      props.totals.original.shipping,
              );

    const tax = productTax + shippingTax;

    return {
        product,
        shipping,
        productTax,
        shippingTax,
        tax,
        total: product + shipping + tax,
    };
});

const shippingNotRefundable = computed(
    () => props.totals.balance.shipping === 0,
);

function openModal() {
    open.value = true;
    nextTick(() => refs.product_refund.value.focus());
}
</script>
